import React, {
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from "react";
import Select from "react-select";
import FileSaver from "file-saver";
import moment from "moment-timezone";
import MaterialTable from "material-table";
import { Col, Row, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Help } from "@material-ui/icons";

import { dirname } from "../../../util/resourcePath";
import { isMakroSite, getMakroInvoiceNo } from "../../../util/siteFunctions";
import { useFormatter } from "../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import usePeakLabels from "../../../util/peakLabels";
import Axios from "axios";

import ApprovalContent from "./ApprovalContent";
import EmailConfirm from "./EmailConfirm";
import InvoicePreview from "./InvoicePreview";
import LoaderGraph from "../../general/LoaderGraph";
import VersionHistoryModal from "./VersionHistoryModal";
import {
	localTimeToPickerTime,
	pickerTimeTolocalTime,
} from "../../../util/momentTimezone";
import {
	allEntityOpt,
	buildInvoiceReceiptNumber,
	formatOldEntityId,
	oldEntityFilter,
} from "../../../util/invoice";
import {
	isAcctUser,
	isClientHQUser,
	isITUser,
	isManagementUser,
	isOMUser,
	isGYTHUser,
	isClientTHUser,
	isMakroClientUser,
	isCFWClientUser,
	isDoHomeClientUser,
	isGYUser,
	isAbleToSendEmailTH,
	isGYVNUser,
} from "../../../util/roles";
import { Popover, Spin, message } from "antd";
import { useUserProps } from "../../../util/userDBContext";
import ExtraLineForm from "./ExtraLineForm";
import "../../admin/IssueListComps/issueFormCss.css";
import "../admin.css";

import _, { isArray } from "lodash";
import dayjs from "dayjs";

const solarCalc = useSolarCalc();

function InvoiceTable(props) {
	const user = useUser();
	const userInfo = props.userInfo;
	const { loggedCountry } = useUserProps();

	const [formatNumber] = useFormatter();

	const directory = dirname();

	const [previewIsHidden, setPreviewIsHidden] = useState(true);
	const [formIsHidden, setFormIsHidden] = useState(true);
	const [invoiceSites, setInvoiceSites] = useState([]);
	const [columns, setColumns] = useState([]);
	const [isTechReport, setIsTechReport] = useState(false);
	const [showInputForm, setShowInputForm] = useState(false);
	const [approvalIsExpired, setApprovalIsExpired] = useState(true);
	const [previewInvoice, setPreviewInvoice] = useState(null);
	const [selectedMonth, setSelectedMonth] = useState(props.selectedMonth);
	const [selectedSite, setSelectedSite] = useState(null);
	const [showGraph, setShowGraph] = useState("");
	const [showRemark, setShowRemark] = useState("");
	const [position, setPosition] = useState(null);
	const [process, setContext] = useState("");
	const [country, setCountry] = useState(loggedCountry);
	const peakLabels = usePeakLabels(country);

	// ----------------------- Server-side preview ----------------------
	const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewFileName, setPreviewFileName] = useState("");

	// ----------------------- Credit Note ----------------------
	const [isCreditNote, setIsCreditNote] = useState(false);

	// ----------------------- Version History ----------------------
	const [versionHistoryInvoice, setVersionHistoryInvoice] = useState(null);
	const showCreditNote = useMemo(
		() => selectedMonth.format("YYYY-MM") === "2023-09",
		[selectedMonth],
	);
	const isCreditNoteSite = useCallback(
		(site) =>
			site.country === "TH" &&
			site.operation_status === 3 &&
			site.client_id !== 18 &&
			site.client_id !== 69 &&
			!/^demo_/g.test(site.table_name),
		[],
	);
	// ----------------------- End Credit Note ------------------

	const showVatInvoiceForm = useMemo(
		() =>
			previewInvoice?.approvalStatus === 2 &&
			selectedSite?.value?.country === "VN",
		[previewInvoice, selectedSite],
	);

	const engYear = parseInt(selectedMonth.format("YYYY"));
	const thYear = engYear + 543;

	const roles = user.roles;

	const [emailConfirmShown, setEmailConfirmShown] = useState(false);
	const [emailsRecipients, setEmailsRecipients] = useState([]);
	const [existingEmailLog, setExistingEmailLog] = useState(null);
	const [reSaveRecipients, setReSaveRecipients] = useState(false);
	const emailsSite = useRef(null);
	// ----------------------- Entity Filter ----------------------
	const [entityOpts, setEntityOpts] = useState(null);
	const [selectedEntity, setSelectedEntity] = useState(allEntityOpt);
	const matTbRef = useRef(null); // for resetting material table component
	// clear selected rows when country or entity select changes
	useEffect(() => {
		setSelectedRows([]);
		if (matTbRef && matTbRef.current) {
			matTbRef.current.onAllSelected(false);
		}
	}, [country, selectedEntity]);

	useEffect(() => {
		if (country === "VN" && !entityOpts) {
			GYAxios.get("/select/entities/VN").then((res) => {
				const entities = res.data.filter(oldEntityFilter);
				setEntityOpts([allEntityOpt, ...entities]);
			});
		}
	}, [country, entityOpts]);

	useEffect(() => {
		if (country == "TH") {
			GYAxios.get(
				"/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
			).then((res) => {
				if (res.status == 200) {
					setExistingEmailLog(res.data);
				} else {
					setExistingEmailLog(null);
					Noti.create({
						type: "e",
						msg: "Failed to get Email log - Button for emailing cannot be used!",
					});
				}
			});
		}
	}, [country, selectedMonth]);

	const invoicesFilter = useCallback(
		(invoices) => {
			return invoices.filter((invoice) => {
				if (country !== "VN") return true;
				if (selectedEntity.value === "_ALL") return true;
				// only for VN invoice and when entity is selected
				return formatOldEntityId(invoice.entity_id) === selectedEntity.value;
			});
		},
		[country, selectedEntity],
	);

	// ----------------------- Bulk actions -----------------------
	const userInvSameCountry = useMemo(
		() => country === loggedCountry,
		[country, loggedCountry],
	);
	const isShownBulkActions = useMemo(
		() =>
			(isClientTHUser(roles) && isClientHQUser(userInfo)) ||
			(isGYVNUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
			(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
			isITUser(roles),
		[roles, userInfo, userInvSameCountry],
	);
	const isShownCombinedDownload = useMemo(
		() =>
			country === "TH" &&
			((isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry],
	); // hide combined download button from client HQ TH and VN ACCT
	const isShownSeparateDownload = useMemo(
		() =>
			country === "TH" &&
			((isClientTHUser(roles) && isClientHQUser(userInfo)) ||
				(isGYTHUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isOMUser(roles) && userInvSameCountry) ||
				(isGYTHUser(roles) && isManagementUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry, userInfo],
	); // hide separated download button from VN ACCT
	const isShownClientGroupedDownload = useMemo(
		() =>
			country === "VN" &&
			((isGYVNUser(roles) && isAcctUser(roles) && userInvSameCountry) ||
				isITUser(roles)),
		[country, roles, userInvSameCountry],
	); // hide separated download button from TH users
	const [isBulkDownloading, setIsBulkDownloading] = useState(false); // flag to show download progress
	const [bulkDownloadProgress, setBulkDownloadProgress] = useState(null); // show download progress text
	const [bulkDownloadProgressWarning, setBulkDownloadProgressWarning] =
		useState(null); // show download progress warning if the download is too slow
	const clickedBulkAction = useRef(null); // flag to check which bulk action is clicked, COMBINED or SEPARATED
	const BULK_ACTIONS = useMemo(
		() => ({
			COMBINED: "COMBINED",
			SEPARATED: "SEPARATED",
			CLIENT_GROUPED: "CLIENT_GROUPED",
		}),
		[],
	); // bulk action types
	const BULK_DOC_TYPES = useMemo(
		() => ({
			INVOICE: "INVOICE",
			MAKRO_INVOICE: "MAKRO_INVOICE",
			CFW_INVOICE: "CFW_INVOICE",
			BILLING_DETAIL: "BILLING_DETAIL",
			TECH_REPORT: "TECH_REPORT",
		}),
		[],
	);
	const INVOICE_COPY_TYPES = useMemo(
		() => ({ ORIGINAL: "ORIGINAL", COPY: "COPY" }),
		[],
	);
	const onBeforeUnload = useCallback(
		() => !!bulkDownloadProgress || undefined,
		[bulkDownloadProgress],
	);
	// get selected sites from material table
	const [selectedRows, setSelectedRows] = useState([]);
	const makroClientId = useMemo(() => 18, []);
	const CFWClientId = useMemo(() => 299, []);
	const DoHomeClientId = useMemo(() => 386, []);
	const makroTestSites = useMemo(() => ["makro_ladkrabang", "makro_ram24"], []);
	const getSelectedSites = () =>
		selectedRows.filter(
			(invoice) =>
				(invoice.country === "TH" || invoice.country === "VN") &&
				((invoice.country === "TH" && invoice.approvalStatus === 3) ||
					invoice.country === "VN") &&
				!makroTestSites.includes(invoice.table_name) &&
				invoice.table_name !== "demo_thailand" &&
				invoice.table_name !== "demo_vietnam" &&
				invoice.tableData.checked,
		);
	const selectedMonthStr = useMemo(
		() => props.selectedMonth.format("YYYY-MM"),
		[props.selectedMonth],
	);
	// reset bulk download states
	const resetBulkDownloadStates = () => {
		setIsBulkDownloading(false);
		setBulkDownloadProgress(null);
		clickedBulkAction.current = null;
		setReSaveRecipients(false);
		setEmailConfirmShown(false);
		setEmailsRecipients([]);
		emailsSite.current = null;
	};
	// ============================================================================
	// Server-side bulk download function (replaces old client-side pipeline)
	// ============================================================================
	const onBulkDownloadServerSide = async (bulkAction) => {
		setIsBulkDownloading(true);
		clickedBulkAction.current = bulkAction;
		const selectedSites = getSelectedSites();

		if (!selectedSites.length) {
			resetBulkDownloadStates();
			if (selectedRows.length) {
				message.warning(
					"Invoice(s) you selected have not yet been approved. You can only download approved invoices.",
				);
			} else {
				message.warning("Please select invoice(s) before downloading them.");
			}
			return;
		}

		setBulkDownloadProgress({
			text: "Generating documents on the server...",
			allCount: selectedSites.length,
			loadedCount: 0,
			startTime: new Date().getTime(),
		});

		try {
			// Build request payload
			const sites = selectedSites.map((site) => ({
				table_name: site.table_name,
				dateData: site.dateData,
				site_id: site.site_id,
				site_code: site.site_code,
				client_id: site.client_id,
				entity_id: site.entity_id,
				country: site.country,
				operation_status: site.operation_status,
				invoice_no: site.invoice_no,
			}));

			// Determine if all Makro sites are selected
			let makroPhases = null;
			if (
				country === "TH" &&
				(isGYTHUser(roles) ||
					(isClientTHUser(roles) &&
						isClientHQUser(userInfo) &&
						isMakroClientUser(userInfo)))
			) {
				const allMakroSites = invoiceSites.filter(
					(site) =>
						site.client_id === makroClientId &&
						!makroTestSites.includes(site.table_name),
				);
				const allMakroSelected = allMakroSites.every((site) =>
					selectedSites.find((s) => s.site_id === site.site_id),
				);
				if (allMakroSelected && allMakroSites.length > 0) {
					const p1Sites = props.summaryInvoiceSites.filter(
						props.makroSitesFilterByPhaseFn("PHASE_1"),
					);
					const p2Sites = props.summaryInvoiceSites.filter(
						props.makroSitesFilterByPhaseFn("PHASE_2"),
					);
					makroPhases = {};
					if (p1Sites.length)
						makroPhases.PHASE_1 = p1Sites.map((s) => s.table_name);
					if (p2Sites.length)
						makroPhases.PHASE_2 = p2Sites.map((s) => s.table_name);
				}
			}

			// Determine if all CFW sites are selected
			let cfwPhases = null;
			if (
				country === "TH" &&
				(isGYTHUser(roles) ||
					(isClientTHUser(roles) &&
						isClientHQUser(userInfo) &&
						isCFWClientUser(userInfo)))
			) {
				const allCFWSites = invoiceSites.filter(
					(site) => site.client_id === CFWClientId,
				);
				const allCFWSelected = allCFWSites.every((site) =>
					selectedSites.find((s) => s.site_id === site.site_id),
				);
				if (allCFWSelected && allCFWSites.length > 0) {
					const gys6Sites = props.summaryInvoiceSites.filter(
						props.CFWSitesFilterByPhaseFn("PHASE_GYS6"),
					);
					const s4Sites = props.summaryInvoiceSites.filter(
						props.CFWSitesFilterByPhaseFn("PHASE_S4"),
					);
					cfwPhases = {};
					if (gys6Sites.length)
						cfwPhases.PHASE_GYS6 = gys6Sites.map((s) => s.table_name);
					if (s4Sites.length)
						cfwPhases.PHASE_S4 = s4Sites.map((s) => s.table_name);
				}
			}

			// Determine if all DoHome sites are selected
			let dohomePhases = null;
			if (
				country === "TH" &&
				(isGYTHUser(roles) ||
					(isClientTHUser(roles) &&
						isClientHQUser(userInfo) &&
						isDoHomeClientUser(userInfo)))
			) {
				const allDoHomeSites = invoiceSites.filter(
					(site) => site.client_id === DoHomeClientId,
				);
				const allDoHomeSelected = allDoHomeSites.every((site) =>
					selectedSites.find((s) => s.site_id === site.site_id),
				);
				if (allDoHomeSelected && allDoHomeSites.length > 0) {
					const dohomeSites = props.summaryInvoiceSites.filter(
						(s) => s.client_id === DoHomeClientId,
					);
					dohomePhases = {};
					dohomeSites.forEach((s) => {
						const entityId = s.entity_id || "TH";
						if (!dohomePhases[entityId]) dohomePhases[entityId] = [];
						dohomePhases[entityId].push(s.table_name);
					});
				}
			}

			const payload = {
				mode: bulkAction.toLowerCase(),
				sites,
				makroPhases,
				cfwPhases,
				dohomePhases,
				month: selectedMonthStr,
				freeSites: props.freeSites || [],
				sortByBillingAddress: true,
			};

			const response = await GYAxios.post("/invoice/pdf/bulk", payload, {
				responseType: "arraybuffer",
				timeout: 20*60*1000,
			});

			// Determine filename from Content-Disposition or fallback
			const contentDisposition =
				response.headers["content-disposition"] || "";
			let fileName = "download";
			const match = contentDisposition.match(
				/filename[^;=\n]*=([\"\']*)(.*?)\1(;|$)/,
			);
			if (match && match[2]) {
				fileName = decodeURIComponent(match[2]);
			} else {
				const mode = bulkAction.toLowerCase();
				const dateStr = selectedMonthStr;
				if (mode === "combined") {
					fileName = `combined-${dateStr}.pdf`;
				} else {
					fileName = `${mode === "client_grouped" ? "client_grouped" : "separate"}_${dateStr}.zip`;
				}
			}

			FileSaver.saveAs(new Blob([response.data]), fileName);
		} catch (err) {
			console.error("[bulk server-side]", err);
			const msg =
				err.response?.data?.message ||
				err.message ||
				"Bulk download failed. Please contact IT.";
			Noti.create({ type: "e", msg });
		} finally {
			resetBulkDownloadStates();
		}
	};

	useEffect(() => {
		window.onbeforeunload = onBeforeUnload;
	}, [onBeforeUnload]);

	useEffect(() => {
		if (!bulkDownloadProgress) {
			return setBulkDownloadProgressWarning("");
		}
		if (bulkDownloadProgress) {
			const { loadedCount, startTime } = bulkDownloadProgress;
			if (
				!bulkDownloadProgressWarning &&
				loadedCount &&
				(new Date().getTime() - startTime) / 1000 > loadedCount * 2 * 1.3
			) {
				// it normally takes 2s to download 1 document, if it takes 30% more time, show warning
				setBulkDownloadProgressWarning(
					"It took longer than expected to download the documents. Please check your internet connection.",
				);
			}
			if (
				bulkDownloadProgressWarning &&
				loadedCount &&
				(new Date().getTime() - startTime) / 1000 <= loadedCount * 2 * 1.3
			) {
				// if it takes less time, remove warning
				setBulkDownloadProgressWarning("");
			}
		}
	}, [bulkDownloadProgress, bulkDownloadProgressWarning]);

	// ----------------------- Finish Bulk actions -----------------------

	// View invoices/reports of current month role
	const viewCurrMonth = roles.includes(
		"administration:InvoiceTable:_viewInvCurMonth",
	);
	// Clients' access to invoice role
	const clientViewInv = roles.includes(
		"administration:InvoiceTable:_clientViewInv",
	);

	// Min date of datepicker
	// Client TH		: 2021-04-01
	// Client VN		: 2021-06-01
	// GY TH			 	: 2020-11-01
	// GY VN			 	: 2021-06-01
	let minDate = "";

	switch (country) {
		case "TH":
			if (roles != null && clientViewInv)
				minDate = moment("2021-04-01", "YYYY-MM-DD").toDate();
			else minDate = moment("2020-11-01", "YYYY-MM-DD").toDate();
			break;

		case "VN":
			minDate = moment("2021-06-01", "YYYY-MM-DD").toDate();
			break;

		case "PH":
			minDate = moment("2025-06-01", "YYYY-MM-DD").toDate();
			break;

		default:
			break;
	}

	// Max date of datepicker
	// Regular		: Previous month
	// With Role	: Current month
	const maxDate =
		roles != null && viewCurrMonth
			? moment().add(1, "month").startOf("month").toDate()
			: moment().startOf("month").toDate();

	// Approval permissions
	const approvalPermissions = props.approvalPermissions;

	// Months
	const months = [
		{ engMonth: "January", thMonth: "มกราคม", phMonth: "Enero" },
		{ engMonth: "February", thMonth: "กุมภาพันธ์", phMonth: "Pebrero" },
		{ engMonth: "March ", thMonth: "มีนาคม", phMonth: "Marso" },
		{ engMonth: "April ", thMonth: "เมษายน", phMonth: "Abril" },
		{ engMonth: "May", thMonth: "พฤษภาคม", phMonth: "Mayo" },
		{ engMonth: "June", thMonth: "มิถุนายน", phMonth: "Hunyo" },
		{ engMonth: "July", thMonth: "กรกฎาคม", phMonth: "Hulyo" },
		{ engMonth: "August", thMonth: "สิงหาคม", phMonth: "Agosto" },
		{ engMonth: "September", thMonth: "กันยายน", phMonth: "Setyembre" },
		{ engMonth: "October", thMonth: "ตุลาคม", phMonth: "Oktubre" },
		{ engMonth: "November", thMonth: "พฤศจิกายน", phMonth: "Nobyembre" },
		{ engMonth: "December", thMonth: "ธันวาคม", phMonth: "Disyembre" },
	];

	// Month quarters
	const quartersMonth = [
		{
			engQuarter: `January ${engYear} - April ${engYear}`,
			thQuarter: `มกราคม ${thYear} - เมษายน ${thYear}`,
		},
		{
			engQuarter: `May ${engYear} - August ${engYear}`,
			thQuarter: `พฤษภาคม ${thYear} - สิงหาคม ${thYear}`,
		},
		{
			engQuarter: `September ${engYear} - December ${engYear}`,
			thQuarter: `กันยายน ${thYear} - ธันวาคม ${thYear}`,
		},
	];

	// Country select options
	const countries = [
		{ label: "Thailand", value: "TH" },
		{ label: "Vietnam", value: "VN" },
		{ label: "Cambodia", value: "KH" },
		{ label: "Philippines", value: "PH" },
	];

	// Country select style
	const selectStyle = {
		container: (provided, state) => ({
			...provided,
			marginRight: 0,
			width: "35%",
			float: "right",
			marginLeft: "1em",
			zIndex: 100,
		}),
		option: (provided, state) => ({
			...provided,
			fontSize: "13px",
		}),
		menuList: (provided, state) => ({
			...provided,
			maxHeight: "500px",
			color: "black",
			textAlign: "left",
		}),
		groupHeading: (provided, state) => ({
			...provided,
			fontSize: "13px",
			color: "#a4aaaa",
			borderTop: "#a4aaaa 1px solid",
		}),
		singleValue: (provided, state) => {
			const fontSize = "13px";
			return { ...provided, fontSize };
		},
	};

	/**
	 * Get phase of Makro sites
	 * @param {Site code of Makro sites} siteCode
	 */
	const getPhase = (siteCode) => {
		let phase = "";
		if (siteCode.includes("MKO")) {
			if (siteCode.length === 5) phase = "(P1)";
			else if (siteCode.length === 6) phase = "(P2)";
		}
		return phase;
	};

	// NOTE: The code of this function is copied and a bit modified from useEffect in InvoicePreview.js
	// where it reshapes selectedSite in useEffect (dependencies: props.selecetedSite, props.isTechReport)
	const getSiteData = async (selectedSite) => {
		const date = moment(selectedSite.value.date, "DD/MM/YYYY");
		const selectedMonth = date.format("YYYYMM");
		const tempSelected = selectedSite;

		const site = tempSelected.value;
		const tableName = tempSelected.table_name;
		const contractID = site.contractID != null ? site.contractID : "";

		// Site is FIT site
		const isFITSite = contractID.includes("FIT");

		// Request to get FIT price
		/* const reqFITPrice = GYAxios.get(`/reports/price/FIT/${selectedMonth}`); */
		// prepare an array to add to GYAxios
		const reqFITPrice = "/reports/price/FIT/" + selectedMonth;
		const reqEVNInput = `/evn_input/site/${tableName}/month/${selectedMonth}`;
		const reqGetBillingChanged = `/invoice/billing_info/${tableName}/${selectedMonth}`;
		const reqGetOldTariff = `/invoice/old_tariff/${selectedMonth}?tariff_id=${site.tariffData.tariffID}`;

		let FITPrices = { FITOn: 0, FITOff: 0, FITStd: null };
		const FITPriceData = await GYAxios.get(reqFITPrice);
		if (!FITPriceData.error) FITPrices = FITPriceData.data[0];

		let EVNInputs = null;
		let moreEVNInputs = null;
		if (isFITSite) {
			const EVNInputsData = await GYAxios.get(reqEVNInput);
			if (EVNInputsData != null && !EVNInputsData.error) {
				if (
					Array.isArray(EVNInputsData.data) &&
					EVNInputsData.data.length > 0
				) {
					EVNInputs = EVNInputsData.data[0];
					if (EVNInputsData.data.length > 1) {
						moreEVNInputs = EVNInputsData.data; // an array to contain many periods of EVN records for a specific month
					}
				}
			}
		}

		let isBillingChanged = false;
		if (site.additional_info != null) {
			if (site.additional_info.invoice != null) {
				if (site.additional_info.invoice.billing_changed != null) {
					isBillingChanged = site.additional_info.invoice.billing_changed;
				}
			}
		}

		let billingChangedData = null;
		if (isBillingChanged) {
			const billingChangedDataRes = await GYAxios.get(reqGetBillingChanged);
			if (billingChangedDataRes != null && !billingChangedDataRes.error) {
				if (
					Array.isArray(billingChangedDataRes.data) &&
					billingChangedDataRes.data.length > 0
				) {
					billingChangedData = billingChangedDataRes.data[0];
				}
			}
		}

		let oldTariffData = null;
		if (site.old_price) {
			const oldTariffDataRes = await GYAxios.get(reqGetOldTariff);
			if (oldTariffDataRes != null && !oldTariffDataRes.error) {
				if (
					Array.isArray(oldTariffDataRes.data) &&
					oldTariffDataRes.data.length > 0
				) {
					oldTariffData = oldTariffDataRes.data[0];
				}
			}
		}

		const siteValues = {
			...tempSelected.value,
			...FITPrices,
			EVNInputs,
			moreEVNInputs,
			billingName:
				billingChangedData != null
					? billingChangedData.billing_name
					: site.billingName,
			billingAddress:
				billingChangedData != null
					? billingChangedData.billing_address
					: site.billingAddress,
			old_price: oldTariffData != null ? oldTariffData : null,
		};

		const finalRes = {
			...tempSelected,
			value: siteValues,
		};
		return finalRes;
	};

	// NOTE: The code of this function is partially copied from generateInvoice() in InvoiceTemplate_VN.js
	// this block of code is also modified to comply with the requirements of Payment Records
	const calculateSubtotalVatTotal = async (siteData, previewInvoice) => {
		let _siteData = siteData;
		const isHavingModifiedTariff = _siteData.value.old_price != null;
		const clientAmount = { subtotal: 0, vat_amount: 0, total: 0 };
		let govAmount = null;

		const data = siteData.value;
		const contractID = data.contractID;
		// Date gate: VN new 3-tier tariff effective 2027-01-01
		const useNewTariff = moment(data.dateStr, ["MMM-YYYY","YYYY-MM","YYYYMM"], true)
			.isSameOrAfter(moment("2027-01", "YYYY-MM"));
		const EVNInputs = data.EVNInputs
			? { ...data.EVNInputs, FITOn: data.FITOn, FITOff: data.FITOff, FITStd: data.FITStd ?? null }
			: null;
		// NOTE: comment out below lines because payment record requires different flow of calculation
		// revenue sharing
		// let SRParams = null;
		// if(EVNInputs) {
		// 	SRParams = solarCalc.calculateSharedRevenue(
		// 		data.contractID,
		// 		data.discountFT,
		// 		EVNInputs.EVN_total,
		// 		EVNInputs.FITOn
		// 	);
		// }
		// const SRSubtotal = SRParams ? SRParams.subtotal : 0;

		const getPriceObj = (data) =>
			data.contractID === "FIT"
				? { priceON: data.FITOn, priceOFF: data.FITOff }
				: { priceON: data.priceON, priceOFF: data.priceOFF };
		const priceObj = getPriceObj(data);
		const price = {
			ON: priceObj.priceON,
			OFF: priceObj.priceOFF,
			// For FIT: STD tier uses FITStd price; for SC: uses priceSTD
			STD: contractID === "FIT" ? (data.FITStd ?? null) : (data.priceSTD ?? null),
			FT: data.FTcost,
		};

		const discount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			STD: data.discountSTD ?? null,
			FT: data.discountFT,
		};

		const currency = data.currency;

		if (isHavingModifiedTariff) {
			const {
				costObj: { costsObj_prev, costsObj_curr },
			} = await calculateForChangingTariff({
				table_name: _siteData.table_name,
				contractID,
				EVNInputs,
				old_price: data.old_price,
				dateStr: data.dateStr,
				FTcost: data.FTcost,
				prices_curr: price,
				discount,
				currency,
				moreEVNInputs: data.moreEVNInputs,
			});

			if (contractID.includes("FIT")) {
				// FIT, SCFITCL, SCFITGY
				clientAmount.subtotal =
					costsObj_prev.costON +
						costsObj_prev.costOFF +
						(useNewTariff ? (costsObj_prev.costSTD ?? 0) : 0) +
						costsObj_curr.costON +
						costsObj_curr.costOFF +
						(useNewTariff ? (costsObj_curr.costSTD ?? 0) : 0) +
						data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}

			if (contractID === "SC") {
				clientAmount.subtotal =
					costsObj_prev.costTotal +
						costsObj_curr.costTotal +
						data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
		} else {
			const prodData = {
				solarON: data.solarON,
				solarOFF: data.solarOFF,
				solarTotal: data.solarTotal,
				solarSTD_temp: data.solarSTD_temp ?? null,
				contractID: data.contractID,
			};
			const prodObj = solarCalc.calculateProd(prodData, EVNInputs);
			const prod = {
				ON: prodObj.solarON,
				OFF: prodObj.solarOFF,
				STD: prodObj.solarSTD ?? null,
				TOTAL: prodObj.solarTotal,
			};

			// calculate subtotal, VAT, total based on contract ID
			if (contractID.includes("FIT")) {
				// FIT, SCFITCL, SCFITGY
				const costsObj = solarCalc.calculateProdCost(
					prod,
					price,
					discount,
					currency,
				);
				clientAmount.subtotal =
					costsObj.costON + costsObj.costOFF + (useNewTariff ? (costsObj.costSTD ?? 0) : 0) + data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
			if (contractID === "SC") {
				const costsObj = solarCalc.calculateProdCost(
					prod,
					price,
					discount,
					currency,
				);
				clientAmount.subtotal = costsObj.costTotal + data.specialDiscount ?? 0;
				clientAmount.vat_amount = clientAmount.subtotal * data.vat;
				clientAmount.total = clientAmount.subtotal + clientAmount.vat_amount;
			}
		}

		// for government payment record only (in payment record, it will have proforma invoice number suffixed with "02")
		if (contractID === "SCFITGY") {
			govAmount = {};
			const _EVNs = data.moreEVNInputs ? data.moreEVNInputs : [EVNInputs];
			const EVN_total = _EVNs.reduce((acc, curr) => acc + (curr?.EVN_total || 0), 0);
			const SRParams = solarCalc.calculateSharedRevenue(
				contractID,
				data.discountFT,
				EVN_total,
				EVNInputs.FITOn,
			);
			govAmount.sharingRevenueAmount = previewInvoice.items?.find(item => item.type === 'shared_revenue')?.line_total || SRParams.subtotal;
			if (useNewTariff) {
				// 3-tier: EVN_on * FITOn + (EVN_off - EVN_std_temp) * FITOff + EVN_std_temp * FITStd
				const evnTotals = {
					EVN_on: _EVNs.reduce((acc, curr) => acc + (curr?.EVN_on || 0), 0),
					EVN_off: _EVNs.reduce((acc, curr) => acc + ((curr?.EVN_off || 0) - (curr?.EVN_std_temp || 0)), 0),
					EVN_std_temp: _EVNs.reduce((acc, curr) => acc + (curr?.EVN_std_temp || 0), 0),
				};
				const gov3Tier = solarCalc.calculateGovRevenue3Tier(evnTotals, {
					FITOn: EVNInputs.FITOn,
					FITOff: EVNInputs.FITOff,
					FITStd: data.FITStd,
				});
				govAmount.subtotal = gov3Tier.subtotal + (data.specialDiscount ?? 0);
			} else {
				govAmount.subtotal = EVN_total * EVNInputs.FITOn + (data.specialDiscount ?? 0);
			}
			govAmount.vat_amount = govAmount.subtotal * data.vat;
			govAmount.total = govAmount.subtotal + govAmount.vat_amount;
		}

		return { clientAmount, govAmount };

		// subtotal
		// NOTE: comment out below lines because payment record
		// let subtotalPrice = isSCFIT
		// 	? solarCalc.parseFormatNumber(costsObj.costTotal + SRSubtotal)
		// 	: costsObj.costTotal;
		// let subtotalPrice = costsObj.costTotal;

		// // VAT amount
		// const vatPercent = data.vat;
		// const VATObj = solarCalc.calculateVAT(
		// 	subtotalPrice,
		// 	data.currency,
		// 	vatPercent
		// );
		// const VATPrice = VATObj.VAT;

		// // total
		// const tempTotal = VATObj.totalDue;
		// // NOTE: comment out below lines because VN doesn't have special discount
		// // let discountPrice = 0;
		// // let totalDue = 0;
		// // if (data.specialDiscount === null) {
		// // 	totalDue = tempTotal;
		// // } else {
		// // 	// Special discounts
		// // 	discountPrice = -(tempTotal * data.specialDiscount);
		// // 	totalDue =
		// // 		parseFloat(subtotalPrice.toFixed(2)) +
		// // 		parseFloat(VATPrice.toFixed(2)) +
		// // 		parseFloat(discountPrice.toFixed(2));
		// // }
		// const totalDue = tempTotal;

		// const subtotal = subtotalPrice;
		// const vat_amount = VATPrice;
		// const total = totalDue;
		// return { subtotal, vat_amount, total };
	};

	const handleRecordPaymentVN = async (vatInvoiceVN, previewInvoice) => {
		const proforma_inv_no = selectedSite.value.invNo;
		const credit_term = selectedSite.value.creditTerm;
		const updated_by = user.email;
		const { vat_inv_no, vat_inv_date } = vatInvoiceVN;
		const siteData = await getSiteData(selectedSite);
		const {
			govAmount,
		} = await calculateSubtotalVatTotal(siteData, previewInvoice);
		const subtotal = previewInvoice.subtotal;
		const vat_amount = previewInvoice.vatAmount;
		const total = previewInvoice.totalAmount;
		const contract_id = previewInvoice.contract_id;

		await GYAxios.post("/payment_record_vn/", {
			proforma_inv_no,
			credit_term,
			updated_by,
			vat_inv_date,
			vat_inv_no,
			subtotal,
			vat_amount,
			total,
			contract_id,
			govAmount,
		});
	};

	const calculateForChangingTariff = async (siteObject) => {
		const table_name = siteObject.table_name;
		const contractID = siteObject.contractID;
		const EVNInputs = siteObject.EVNInputs;
		const FTcost = siteObject.FTcost;
		const discount = siteObject.discount;
		const currency = siteObject.currency;
		const prices_curr = siteObject.prices_curr;

		let moreEVNInputs =
			siteObject.moreEVNInputs != null ? siteObject.moreEVNInputs : null;

		const old_price_obj = siteObject.old_price;
		const old_price_start_date = moment(siteObject.dateStr)
			.startOf("month")
			.format("YYYY-MM-DD");
		const old_price_end_date = moment(old_price_obj.end_date).format(
			"YYYY-MM-DD",
		);
		const current_price_start_date = moment(old_price_end_date)
			.add(1, "days")
			.format("YYYY-MM-DD");
		const current_price_end_date = moment(siteObject.dateStr)
			.endOf("month")
			.format("YYYY-MM-DD");
		const url_production = [
			`/invoice/daily_prod?fromDate=${old_price_start_date}&toDate=${old_price_end_date}&table_name=${table_name}`,
			`/invoice/daily_prod?fromDate=${current_price_start_date}&toDate=${current_price_end_date}&table_name=${table_name}`,
		];

		const prodData = await Axios.all([
			GYAxios.get(url_production[0]),
			GYAxios.get(url_production[1]),
		])
			.then(
				Axios.spread((...responses) => {
					const _prodData = responses.map((res) => res.data);
					return _prodData;
				}),
			)
			.catch((errors) => {
				console.log(errors);
			});

		let evn_01 = null;
		let evn_02 = null;

		if (moreEVNInputs != null) {
			evn_01 = moreEVNInputs.filter(
				(evn) =>
					moment(evn.start_date).format("YYYY-MM-DD") == old_price_start_date,
			);
			evn_02 = moreEVNInputs.filter(
				(evn) =>
					moment(evn.start_date).format("YYYY-MM-DD") ==
					current_price_start_date,
			);

			if (evn_01.length === 0) evn_01 = null;
			else evn_01 = evn_01[0];
			if (evn_02.length === 0) evn_02 = null;
			else evn_02 = evn_02[0];
		} else {
			evn_01 = null;
			evn_02 = EVNInputs;
		}

		const prodData_prev = prodData[0][0];
		const prodData_curr = prodData[1][0];

		const _prodData_prev = {
			solarON: prodData_prev.solar_ON,
			solarOFF: prodData_prev.solar_OFF,
			solarTotal: prodData_prev.solar_total,
			solarON_temp: prodData_prev.solar_ON_temp ?? null,
			solarOFF_temp: prodData_prev.solar_OFF_temp ?? null,
			solarSTD_temp: prodData_prev.solar_STD_temp ?? null,
			solarTotal_temp: prodData_prev.solar_total_temp ?? null,
			contractID: contractID,
		};

		const _prodData_curr = {
			solarON: prodData_curr.solar_ON,
			solarOFF: prodData_curr.solar_OFF,
			solarTotal: prodData_curr.solar_total,
			solarON_temp: prodData_curr.solar_ON_temp ?? null,
			solarOFF_temp: prodData_curr.solar_OFF_temp ?? null,
			solarSTD_temp: prodData_curr.solar_STD_temp ?? null,
			solarTotal_temp: prodData_curr.solar_total_temp ?? null,
			contractID: contractID,
		};

		const _evnDataDaily = { _evn_prev: evn_01, _evn_curr: evn_02 };

		if (EVNInputs != null) {
			_evnDataDaily._evn_prev = {
				EVN_on: evn_01?.EVN_on || 0,
				EVN_off: evn_01?.EVN_off || 0,
				EVN_std_temp: evn_01?.EVN_std_temp || 0,
				EVN_total: evn_01?.EVN_total || 0,
			};
			_evnDataDaily._evn_curr = {
				EVN_on: evn_02?.EVN_on || 0,
				EVN_off: evn_02?.EVN_off || 0,
				EVN_std_temp: evn_02?.EVN_std_temp || 0,
				EVN_total: evn_02?.EVN_total || 0,
			};
		}

		/* if (EVNInputs?.needTakenDaily === 1) {
			_evnDataDaily._evn_prev = {
				EVN_on: prodData_prev.EVN_on,
				EVN_off: prodData_prev.EVN_off,
				EVN_total: prodData_prev.EVN_total,
			}
			_evnDataDaily._evn_curr = {
				EVN_on: prodData_curr.EVN_on,
				EVN_off: prodData_curr.EVN_off,
				EVN_total: prodData_curr.EVN_total,
			}
		} */
		const calculated_prod_EVN_prev = solarCalc.calculateProd(
			_prodData_prev,
			_evnDataDaily._evn_prev,
		);
		const calculated_prod_EVN_curr = solarCalc.calculateProd(
			_prodData_curr,
			_evnDataDaily._evn_curr,
		);

		const prodData_prev_ = {
			ON: calculated_prod_EVN_prev.solarON,
			OFF: calculated_prod_EVN_prev.solarOFF,
			STD: calculated_prod_EVN_prev.solarSTD ?? null,
			TOTAL: calculated_prod_EVN_prev.solarTotal,
		};

		const prodData_curr_ = {
			ON: calculated_prod_EVN_curr.solarON,
			OFF: calculated_prod_EVN_curr.solarOFF,
			STD: calculated_prod_EVN_curr.solarSTD ?? null,
			TOTAL: calculated_prod_EVN_curr.solarTotal,
		};

		const prices_prev = {
			ON: old_price_obj.price_on,
			OFF: old_price_obj.price_off,
			STD: old_price_obj.price_std ?? null,
			FT: FTcost,
		};

		const costsObj_prev = solarCalc.calculateProdCost(
			prodData_prev_,
			prices_prev,
			discount,
			currency,
		);
		const costsObj_curr = solarCalc.calculateProdCost(
			prodData_curr_,
			prices_curr,
			discount,
			currency,
		);
		const costObj = { costsObj_prev, costsObj_curr };

		return { costObj };
	};

	const reload = useCallback(async () => {
		props.setReady(false);
		if (country == "TH") {
			GYAxios.get(
				"/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
			).then((res) => {
				if (res.status == 200) {
					setExistingEmailLog(res.data);
				} else {
					setExistingEmailLog(null);
					Noti.create({
						type: "e",
						msg: "Failed to get Email log - Button for emailing cannot be used!",
					});
				}
			});
		}
	}, [country, selectedMonth, props]);

	const refreshEmailLogs = useCallback(() => {
  if (country !== "TH") return;
  GYAxios.get(
    "/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
  )
    .then((res) => {
      if (res.status === 200) setExistingEmailLog(res.data);
    })
    .catch(() => {});
}, [country, selectedMonth]);

	async function updateInvoiceDate(monthly_id) {
		const ques = "Enter the new date for the invoice (format: YYYY-MM-DD)";
		const newDate = prompt(ques);

		const dateFormat = /^\d{4}-\d{2}-\d{2}$/; // Matches 'YYYY-MM-DD'
		if (dateFormat.test(newDate) && monthly_id != null) {
			const isValid = dayjs(newDate, "YYYY-MM-DD", true).isValid();
			if (isValid) {
				await GYAxios.patch(
					`/invoice/update_invoice_date?param[monthly_id]=${monthly_id}&param[invoice_date]=${newDate}`,
				);
				alert("Invoice date updated!");
				applyLocalStatusUpdate(monthly_id, { inv_issued_date: newDate });
			} else {
				// return error message:
				alert("Invalid date format");
			}
		} else {
			// return error message:
			alert("Invalid date format");
		}
	}

	/**
	 * Allow changing the invoice number for unapproved invoices (approvalStatus === 1).
	 * @param {number} monthly_id
	 * @param {string|null} currentInvoiceNo
	 */
	const handleUpdateInvoiceNo = async (monthly_id, currentInvoiceNo) => {
		const ques = `Enter the new invoice number (current: ${currentInvoiceNo || "auto-generated"})`;
		const newInvoiceNo = prompt(ques);
		if (newInvoiceNo === null) return; // cancelled
		if (!newInvoiceNo.trim()) {
			Noti.create({ type: "e", msg: "Invoice number cannot be empty." });
			return;
		}
		try {
			const res = await GYAxios.patch("/reports/update_monthly", {
				id: monthly_id,
				param: { invoice_no: newInvoiceNo.trim() },
			});
			if (res.data?.error) {
				Noti.create({ type: "e", msg: "Failed to update invoice number." });
			} else {
				Noti.create({ type: "s", msg: "Invoice number updated!" });
				applyLocalStatusUpdate(monthly_id, { invoice_no: newInvoiceNo.trim() });
			}
		} catch (err) {
			Noti.create({ type: "e", msg: "Failed to update invoice number." });
		}
	};

	/**
	 * Toggle grid energy reinjection status for a site
	 * @param {Object} site - The site object containing invoice data
	 */
	const handleGridEnergyReinjection = useCallback(
		async (site) => {
			const currentStatus = site.is_grid_reinject_applied === 1;
			const actionText = currentStatus
				? "remove grid energy reinjection data from"
				: "apply grid energy reinjection data to";
			const confirmMessage = `Are you sure you want to ${actionText} this invoice?`;

			if (window.confirm(confirmMessage)) {
				try {
					const response = await GYAxios.post(
						"/invoice/grid_energy_reinjection",
						{
							dateData: moment(site.dateData, "YYYY-MM-DDTHH:mm:ssZ").format(
								"YYYY-MM-DD",
							),
							table_name: site.table_name,
							is_grid_reinject_applied: currentStatus ? 0 : 1,
						},
					);

					if (response.data.success) {
						Noti.create({
							type: "success",
							msg: response.data.message,
						});
						reload(); // Reload the table to reflect changes
					} else {
						Noti.create({
							type: "error",
							msg: "Failed to update grid energy reinjection status",
						});
					}
				} catch (error) {
					console.error("Error updating grid energy reinjection:", error);
					Noti.create({
						type: "error",
						msg:
							error.response?.data?.error ||
							"An error occurred while updating grid energy reinjection status",
					});
				}
			}
		},
		[reload],
	);

		const applyLocalStatusUpdate = useCallback((rowId, changedParam) => {
		setInvoiceSites((prev) =>
			prev.map((s) => {
				if (s.id !== rowId) return s;
				const next = { ...s, ...changedParam };
				if (changedParam.approvalStatus != null) {
					next.approvalStatus = parseInt(changedParam.approvalStatus, 10);
				}
				return next;
			}),
		);
	}, []);

	/**
	 * Update status of invoice
	 *
	 * NOTE: it is here and not in ApprovalContents because of
	 * Noti component. notifications will disappear once
	 * the component is destroyed
	 * @param {'APPROVE'|'REJECT'|'RESOLVE'|'UNAPPROVE'} process
	 * @param {string|null} remark  - reject remark (REJECT only)
	 * @param {object} vatInvoiceVN - VAT invoice fields (APPROVE on VN final step only)
	 */
	const updateInvoiceStatus = useCallback((process, remark, vatInvoiceVN = {}, siteOverride = null) => {
		let param = null;
		let approvalStatus = "";
		let inv_issued_date = "";
		let resMsg = "";

		// siteOverride allows calling this function without first setting previewInvoice state
		const invoiceData = siteOverride ?? previewInvoice;
		if(!invoiceData) {
			Noti.create({ type: "e", msg: "No invoice data available." });
			return;
		}

		const freeSites = props.freeSites;

		const isFreeMakroSite = freeSites.includes(invoiceData.table_name);

		const contractID = invoiceData.contract_id;
		let isFITorSCFITSite = contractID.includes("FIT");
		const approvedStatus = 3;

		switch (process) {
			case "APPROVE":
				approvalStatus = parseInt(invoiceData.approvalStatus) + 1;
				inv_issued_date =
					invoiceData.inv_issued_date == null
						? moment(selectedMonth, "YYYY-MM-DD")
								.endOf("month")
								.format("YYYY-MM-DD")
						: moment(invoiceData.inv_issued_date).format("YYYY-MM-DD");
				if (approvalStatus === 2 && isOMUser(roles) && isGYTHUser(roles)) {
					approvalStatus = parseInt(invoiceData.approvalStatus) + 2;
				}
				
				const invoiceNo = !isMakroSite(invoiceData.client_id)
					? (invoiceData.invoice_no || buildInvoiceReceiptNumber({
							type: "INV",
							solarEntityId: invoiceData.entity_id,
							date: invoiceData.dateData,
							siteCode: invoiceData.site_code,
							isMSA: invoiceData.operation_status === 2,
							clientId: invoiceData.client_id,
						}))
					: getMakroInvoiceNo(
							invoiceData.site_code,
							invoiceData.dateData,
						);
				param = {
					approvalStatus,
					inv_issued_date,
					invoice_no: invoiceNo,
					locked: 1
				};

				const isApprovedByAcct = approvalStatus === approvedStatus;

				// Lock monthly_calc if approved by accounting
				if (isApprovedByAcct) {
					invoiceData.invoiceNo = invoiceNo;
				}

				resMsg = "Invoice approved!";
				break;

			case "UNAPPROVE": {
				// Determine new status: TH 3→1, VN/PH 3→2 or 2→1
				const unapproveCountry = invoiceData.country;
				const currentStatus = parseInt(invoiceData.approvalStatus);
				if (unapproveCountry === "TH" && currentStatus === 3) {
					approvalStatus = 1;
				} else if (
					(unapproveCountry === "VN" || unapproveCountry === "PH") &&
					(currentStatus === 3 || currentStatus === 2)
				) {
					approvalStatus = currentStatus - 1;
				} else {
					break; // unsupported state
				}
				param = { approvalStatus, locked: 0, invoice_no: null, inv_issued_date: null };
				resMsg = "Invoice unapproved!";
				break;
			}

			case "REJECT":
				if (remark != null) {
					const _approvalStatus = isAcctUser ? 1 : -1;
					approvalStatus = parseInt(_approvalStatus);
					param = { rejectedRemark: remark, approvalStatus };
				}
				resMsg = "Invoice rejected!";
				break;
			case "RESOLVE":
				approvalStatus = Math.abs(parseInt(invoiceData.approvalStatus));
				param = { approvalStatus, rejectedRemark: null };
				resMsg = "Invoice resolved!";
				break;

			default:
				break;
		}

		if (param != null) {
			const finalParam = {
				id: invoiceData.id,
				param,
				performed_by_email: userInfo?.email || "",
				performed_by_name: userInfo?.name || userInfo?.email || "",
			};
			const isApprovedByAcct = approvalStatus === approvedStatus;

			try {
				if (isFITorSCFITSite && isApprovedByAcct)
					handleEVNInputs(invoiceData);

				GYAxios.patch("/reports/update_monthly", finalParam).then((res) => {
					if (res.data.error != null) {
						// Show failed notification
						Noti.create({
							type: "e",
							msg: "Data update failed..",
						});
					} else {
						// Show success notification
						Noti.create({
							type: "s",
							msg: resMsg,
						});

						// Refetch updated data
						applyLocalStatusUpdate(invoiceData.id, param);
						refreshEmailLogs();

						// Only for TH sites on final approval
						if (
							invoiceData.country === "TH" &&
							!isFreeMakroSite &&
							approvalStatus === approvedStatus
						)
							handleReceipt(invoiceData);

						// Only for VN sites on final approval (VAT invoice form was shown)
						if (
							showVatInvoiceForm &&
							invoiceData.country === "VN" &&
							approvalStatus === approvedStatus
						) {
							handleRecordPaymentVN(vatInvoiceVN, invoiceData);
						}
					}
				});
			} catch (error) {
				console.log("error :", error);
				// Show failed notification
				Noti.create({
					type: "e",
					msg: "Data update failed..",
				});
			}
		} else {
			// Show error notification
			Noti.create({
				type: "e",
				msg: "Error! Please try again later",
			});
		}
		closeForm();
	}, [previewInvoice, props, selectedMonth, roles, userInfo, userInfo, showVatInvoiceForm, applyLocalStatusUpdate, refreshEmailLogs]);

	/**
	 * Get dept that is to approve invoice based on invoice status
	 * @param {Approval status of invoice} approvalStatus
	 */
	const getApprovalStage = (approvalStatus) => {
		if (approvalStatus === 1) return "O&M";
		else return "Accounting";
	};

	/**
	 * Get department that rejected the invoice
	 * @param {Approval status of invoice} approvalStatus
	 */
	const getRejector = (approvalStatus) => {
		if (approvalStatus === -1) return "O&M";
		else return "Accounting";
	};

	/**
	 * Show under-review status of invoice based on user's permission
	 * @param {Selected site} site
	 */
	const getUnderRevCol = (site) => {
		const approvalStage = getApprovalStage(site.approvalStatus);

		return (
			<span
				className={
					approvalStage === "O&M"
						? "status onm-review glyphicon glyphicon-time"
						: "status acc-review glyphicon glyphicon-time"
				}
				title={`Under review (${approvalStage})`}
			></span>
		);
	};

	/**
	 * Show unapproved status of invoice based on user's permission
	 * @param {Selected site} site
	 */
	const getUnapprovedCol = (site) => {
		// VN final approval (status 2 → 3) still requires the VAT invoice form.
		// All other cases approve immediately without a confirmation popup.
		const isVNFinalApproval =
			site.country === "VN" && parseInt(site.approvalStatus) === 2;

		return (
			<>
				<button
					className="btn btn-primary btn-generate btn-approval"
					title="Approve invoice"
					onClick={() => {
						setPreviewInvoice(site);
						setSelectedSite(formatSelectedSite(site));
						setContext("APPROVE");
						if (isVNFinalApproval) {
							// Open the VAT invoice form
							setIsTechReport(false);
							setIsCreditNote(false);
							setFormIsHidden(false);
							setShowGraph("fadeIn");
						} else {
							updateInvoiceStatus("APPROVE", null, {}, site);
						}
					}}
				>
					<span className="glyphicon glyphicon-ok"></span>
				</button>
				<button
					title="Reject invoice"
					className="btn btn-danger btn-generate"
					onClick={() => {
						setPreviewInvoice(site);
						setSelectedSite(formatSelectedSite(site));
						setContext("REJECT");
						setIsTechReport(false);
						setIsCreditNote(false);
						setFormIsHidden(false);
						setShowGraph("fadeIn");
					}}
				>
					<span className="glyphicon glyphicon-remove"></span>
				</button>
			</>
		);
	};

	/**
	 * Show rejected status of invoice based on user's permission
	 * @param {Selected site} site
	 * @param {Approval permission of user} approvalPermissions
	 */
	const getRejectedCol = (site, approvalPermissions) => {
		const rejector = getRejector(site.approvalStatus);
		const rejectedByMsg = `by ${rejector}`;

		return (
			<>
				<span
					className="status rejected glyphicon glyphicon-remove-sign"
					onMouseEnter={(e) => {
						const x = e.clientX;
						const y = e.clientY;

						setShowRemark({ ...site, rejectedByMsg });
						setPosition({ x, y });
					}}
					onMouseLeave={() => {
						setShowRemark(null);
						setPosition(null);
					}}
					onClick={() => {
						if (approvalPermissions.level === "IT") {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setContext("RESOLVE");
							setIsTechReport(false);
							setIsCreditNote(false);
							setFormIsHidden(false);
							setShowGraph("fadeIn");
						}
					}}
				></span>
			</>
		);
	};

	/**
	 * Show approved status if invoices are approved completely.
	 * Also shows an Unapprove button for users who are allowed to reverse the approval.
	 */
	const getApprovedCol = (site, isApproved = false) => {
		// Determine whether the current user can unapprove this invoice
		// TH      : one-step — TH approval role can unapprove from 3
		// VN / PH : two-step — ACCT can unapprove from 3 (ACCT approval)
		const canUnapprove = (() => {
			if (site.approvalStatus === 3 && site.country === "TH" && roles.includes("administration:InvoiceTable:_approveInvoice-TH")) {
				return true;
			}
			if (site.approvalStatus === 3 && ((site.country === "VN" && roles.includes("_GY:Accounting")) || (site.country === "PH" && roles.includes("_GY:Construction_InvoiceApproval")))) {
				return true;
			}
			return false;
		})();

		return (
			<p style={{ margin: 0 }}>
				<span
					className="status approved glyphicon glyphicon-ok-sign"
					title="Approved"
				></span>
				{canUnapprove && (
					<button
						className="btn btn-warning btn-generate"
						title="Unapprove invoice"
						style={{ marginTop: "4px", padding: "2px 10px" }}
						onClick={() => updateInvoiceStatus("UNAPPROVE", null, {}, site)}
					>
						<span className="glyphicon glyphicon-repeat"></span>
					</button>
				)}
			</p>
		);
	};

	// Show colorful MSA siteName
	const generateSiteName = (site) => {
		return site.operation_status === 2 ? (
			<p
				style={{ color: "#ff8500", fontSize: "inherit", fontFamily: "inherit" }}
			>
				{site.siteName + ` (MSA)`}
			</p>
		) : (
			<p style={{ color: "#666", fontSize: "inherit", fontFamily: "inherit" }}>
				{site.siteName}
			</p>
		);
	};

	/**
	 * Show generate buttons based on roles
	 */
	const getGenerateCol = (site) => {
		// Show invoice btn role
		const showInvoiceBtn = user.roles.includes(
			"administration:InvoiceTable:_viewInvoiceBtn",
		);
		// Show report btn role
		const showReportBtn = user.roles.includes(
			"administration:InvoiceTable:_viewReportBtn",
		);
		//  Show internal invoice btn role
		const showInternalBtn = user.roles.includes(
			"administration:InvoiceTable:_viewInternalBtn",
		);
		const showExtralineBtn =
			site.approvalStatus < 3 &&
			(site.country === "VN" || site.operation_status === 2) &&
			!(site.special_discount && site.special_discount_comment);
		const showEditBtn =
			!!site.special_discount &&
			!!site.special_discount_comment &&
			site.approvalStatus < 3;

		const showModificationDateBtn = site.approvalStatus === 1;
		const showInvoiceNoBtn = site.approvalStatus === 1;

		return (
			<div style={{ width: "fit-content", margin: "auto" }}>
				{showInvoiceBtn && (
					<button
						className="btn btn-generate btn-invoice"
						title="Invoice"
						onClick={() => fetchPreviewPdf(site, false)}
					>
						<span className="glyphicon glyphicon-btc"></span>
					</button>
				)}
				{showReportBtn && (
					<button
						className="btn btn-generate btn-report"
						title="Client Report"
						onClick={() => fetchPreviewPdf(site, true)}
					>
						<span className="glyphicon glyphicon-list-alt"></span>
					</button>
				)}

				{showModificationDateBtn && (
						<button
							className="btn btn-generate btn-report"
							title="Change Invoice Date"
							onClick={() => updateInvoiceDate(site.id)}
						>
							<span className="glyphicon glyphicon-calendar"></span>
						</button>
					)}

				{showInvoiceNoBtn && (
					<button
						className="btn btn-generate btn-info"
						title="Change Invoice Number"
						onClick={() => handleUpdateInvoiceNo(site.id, site.invoice_no)}
					>
						<span className="glyphicon glyphicon-tag"></span>
					</button>
				)}

				<button
					className="btn btn-generate btn-default"
					title="Version History"
					onClick={() => setVersionHistoryInvoice(site)}
				>
					<span className="glyphicon glyphicon-time"></span>
				</button>

				{/* Grid Energy Reinjection Button */}
				{site.country === "TH" &&
					(site.operation_status === 3 || site.operation_status === 2) &&
					user.roles.includes(
						"administration:InvoiceTable:_applyGridEnergyReinjection",
					) && (
						<button
							className={`btn btn-generate btn-grid-energy-reinjection ${
								site.is_grid_reinject_applied === 1
									? "btn-grid-applied"
									: "btn-grid-not-applied"
							}`}
							title={
								site.is_grid_reinject_applied === 1
									? "Grid energy reinjection applied - Click to remove"
									: "Grid energy reinjection not applied - Click to apply"
							}
							disabled={site.approvalStatus !== 1}
							onClick={() => handleGridEnergyReinjection(site)}
						>
							<span className={`glyphicon glyphicon-flash`}></span>
						</button>
					)}

				{showExtralineBtn && (
					<button
						className="btn btn-generate btn-plus"
						title="Extra Line"
						onClick={() => {
							setShowInputForm(true);
							setSelectedSite(formatSelectedSite(site));
						}}
					>
						<span className="glyphicon glyphicon-plus"></span>
					</button>
				)}
				{showEditBtn && (
					<button
						className="btn btn-generate btn-pencil"
						title="Edit Line"
						onClick={() => {
							setShowInputForm(true);
							setSelectedSite(formatSelectedSite(site));
						}}
					>
						<span className="glyphicon glyphicon-pencil"></span>
					</button>
				)}
				{showInternalBtn && (
					<button
						className="btn btn-generate btn-internal"
						title="Internal Report"
						onClick={() => alert("Internal report - COMING SOON")}
					>
						<span className="glyphicon glyphicon-scale"></span>
					</button>
				)}
				{showCreditNote && isCreditNoteSite(site) && (
					<button
						className="btn btn-generate btn-credit-note"
						title="Credit Note"
						onClick={() => {
							setPreviewInvoice(site);
							setSelectedSite(formatSelectedSite(site));
							setIsTechReport(false);
							setIsCreditNote(true);
							setPreviewIsHidden(false);
							setShowGraph("fadeIn");
						}}
					>
						<i class="glyphicon glyphicon-credit-card"></i>
					</button>
				)}
				{country === "TH" &&
					((site.approvalStatus === 3 && site.operation_status === 3) ||
						(site.approvalStatus >= 1 && site.operation_status === 2)) &&
					isAbleToSendEmailTH(roles) && (
						<button
							className={`btn btn-primary btn-generate`}
							title={site.approvalStatus === 3 && site.emailLog ? "E-invoice was sent" : "Send e-invoice"}
							disabled={site.approvalStatus === 3 && site.emailLog}
							onClick={() => {
								findEmailToSend(site);
							}}
						>
							<span className="glyphicon glyphicon-envelope"></span>
						</button>
					)}
			</div>
		);
	};

	/**
	 * Show Status icons based on respective approval permission
	 */
	const getStatusColumn = (site) => {
		const statusStr = site.approvalStatus.toString();
		const roles = user.roles;

		const hasTHApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-TH",
		);

		const hasVNApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-VN",
		);

		const hasPHApprovalRole = roles.includes(
			"administration:InvoiceTable:_approveInvoice-PH",
		);

		let showApproval = false;
		const hasSameCountry = user.country === site.country;

		switch (site.country) {
			case "TH":
				if (hasTHApprovalRole) showApproval = true;
				break;
			case "VN":
				if (hasVNApprovalRole) showApproval = true;
				break;
			case "PH":
				if (hasPHApprovalRole) showApproval = true;
				break;

			default:
				break;
		}
		// Approved status
		const isApproved = site.approvalStatus === 3;
		if (isApproved) return getApprovedCol(site, true);
		// Rejected invoice status
		const REJcodeArr = approvalPermissions.REJcode.split(",");
		const isRejected = REJcodeArr.includes(statusStr);
		if (isRejected) return getRejectedCol(site, approvalPermissions);

		// Approve and Reject buttons for O&M and Acc
		const UNcodeArr = approvalPermissions.UNcode.split(",");
		const isUnapproved = UNcodeArr.includes(statusStr);
		if (isUnapproved && showApproval) return getUnapprovedCol(site);

		// VN / PH O&M unapprove from status 2 → 1.
		// This check is OUTSIDE the isUnderReview block because status 2 may not
		// be in the O&M user's REVcode, which would otherwise skip it entirely.
		const canVNPHOMUnapprove =
			(site.country === "VN" || site.country === "PH") &&
			site.approvalStatus === 2 &&
			(hasVNApprovalRole || hasPHApprovalRole) &&
			!roles.includes("_GY:Accounting") && 
			!roles.includes("_GY:Construction_InvoiceApproval");

		if (canVNPHOMUnapprove) {
			return (
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px" }}>
					{getUnderRevCol(site)}
					<button
						className="btn btn-warning btn-generate"
						title="Unapprove (revert O&M approval)"
						style={{ marginTop: "4px", padding: "2px 10px" }}
						onClick={() => updateInvoiceStatus("UNAPPROVE", null, {}, site)}
					>
						<span className="glyphicon glyphicon-repeat"></span>
					</button>
				</div>
			);
		}

		// Under review status (fallback)
		const REVcodeArr = approvalPermissions.REVcode.split(",");
		const isUnderReview = REVcodeArr.includes(statusStr);
		if (isUnderReview || !hasSameCountry) {
			return getUnderRevCol(site);
		}
	};

	/**
	 * Handle and format invoices sites
	 */
	const handleInvoiceSites = (invoiceSites) => {
		const email_logs = [];

		if (isArray(existingEmailLog)) {
			// take only the distinct email logs
			const distinctEmailLogs = existingEmailLog.filter(
				(v, i, a) => a.findIndex((t) => t.site_id === v.site_id) === i,
			);
			email_logs.push(...distinctEmailLogs);
		}

		// Formatted sites — data comes pre-calculated from server via calculateInvoiceData()
		// displayON/displayOFF/displayTotal are computed server-side (handles FIT, SCFITCL/SCFITGY, etc.)
		const formattedSites =
			invoiceSites != null
				? invoiceSites.map((site) => {
						return {
							...site,
							clientName: `${site.company} ${getPhase(site.site_code)}`,
							siteName: site.site_name,
							contractDesc: site.contractDesc || site.contract_desc,
							contractID: site.contractID || site.contract_id,
							displayON: formatNumber(site.displayON, 3),
							displayOFF: formatNumber(site.displayOFF, 3),
							displayTotal: formatNumber(site.displayTotal, 3),
						};
					})
				: [];

		// adding email logs to the formatted sites
		if (email_logs.length > 0 && formattedSites.length > 0) {
			formattedSites.forEach((site) => {
				const emailLog = email_logs.find((log) => log.site_id == site.site_id);
				if (emailLog) {
					site.emailLog = true;
				} else {
					site.emailLog = false;
				}
			});
		}

		const contractIDs = formattedSites.map((site) => site.contractID);
		const distinctIDs = [...new Set(contractIDs)];

		const hasMultipleContracts = distinctIDs.length > 1 ? true : false;

		// Columns of table
		let titles = [
			{
				title: "Operation Status",
				field: "operation_status",
				headerStyle: { display: "none", width: "0px", maxWidth: "0px" },
				cellStyle: { display: "none", width: "0px", maxWidth: "0px" },
				lookup: { 1: "LIP", 2: "MSA", 3: "COD", 4: "TEST" },
			},
			{ title: "Entity", field: "entity_id", width: 100 },
			{ title: "Client", field: "clientName", width: 170 },
			{
				title: "Site Name",
				field: "siteName",
				grouping: false,
				width: 270,
				render: (site) => generateSiteName(site),
			},
			{
				title: `Prod ${peakLabels[0]} (kWh)`,
				field: "displayON",
				grouping: false,
				width: 120,
			},
			{
				title: `Prod ${peakLabels[1]} (kWh)`,
				field: "displayOFF",
				grouping: false,
				width: 120,
			},
			{
				title: "Total Solar Prod (kWh)",
				field: "displayTotal",
				grouping: false,
				width: 120,
			},
			{
				title: "SF ID",
				field: "SF_project_id",
				grouping: false,
				width: 120,
				headerStyle: { display: "none", width: "0px", maxWidth: "0px" },
				cellStyle: { display: "none", width: "0px", maxWidth: "0px" },
			},
			{
				title: "Generate",
				grouping: false,
				render: (site) => getGenerateCol(site),
			},
		];

		// Country has many contract types
		if (hasMultipleContracts)
			titles.unshift({ title: "Contract", field: "contractDesc" });

		// Show Status column only if the user has approval permission
		// and approval is not expired
		const showStatusColumn = approvalPermissions;

		// Status column config
		const statusColumn = {
			title: "Status",
			field: "approvalStatus",
			grouping: false,
			width: 50,
			render: (site) => getStatusColumn(site),
		};

		// Add status column to first in columns array
		if (showStatusColumn) titles.unshift(statusColumn);

		setColumns(titles);
		setInvoiceSites(formattedSites);
	};

	/**
	 * Add a receipt for corresponding invoices
	 * @param {obj} previewInvoice
	 */
	const handleReceipt = (previewInvoice) => {
		const isAMakroSite = isMakroSite(previewInvoice.client_id);
		let invoiceNo = "";

		if (isAMakroSite) {
			invoiceNo = getMakroInvoiceNo(
				previewInvoice.site_code,
				previewInvoice.dateData,
			);
		} else {
			// Validate if invoice(s) are approved
			invoiceNo =
				previewInvoice.invoiceNo != null ? previewInvoice.invoiceNo : "";
		}

		if (invoiceNo !== "") {
			// Fetch invoice(s) of current invoice number
			const invoicesRequest = GYAxios.get(
				`/reports/monthly_calc/invoice_no/${invoiceNo}`,
			);

			invoicesRequest.then((res) => {
				const response = res.data;
				let invoicesAreApproved = false;

				if (response.error == null)
					// Search in array if there are unapproved invoices
					invoicesAreApproved = !response.find(
						(inv) => inv.approvalStatus !== 3,
					);

				if (invoicesAreApproved) {
					try {
						GYAxios.post(`/receipts/insert/${invoiceNo}`).then((res) => {
							if (res.data.error) {
								Noti.create({
									type: "e",
									msg: "Receipt insert failed..",
								});
							}
						});
					} catch (error) {
						console.log("error :", error);

						// Show failed notification
						Noti.create({
							type: "e",
							msg: "Data update failed..",
						});
					}
				}
			});
		}
	};

	/**
	 * Add a EVN input of corresponding invoice
	 * only for SCFIT and FIT sites.
	 * Inserts only if the next-month entry does not already exist,
	 * so re-approving after unapproval does not create duplicate entries.
	 * @param {obj} invoice
	 */
	const handleEVNInputs = (invoice) => {
		const tableName = invoice.table_name;
		const YYYYMM = moment(invoice.dateData).format("YYYYMM");

		GYAxios.get(`/site/${tableName}/evn_input/month/${YYYYMM}`).then(
			(response) => {
				const EVNInput =
					!response.data.error && response.data.length > 0
						? response.data[0]
						: null;

				if (EVNInput != null) {
					const newStartDate = moment(EVNInput.end_date)
						.add(1, "day")
						.format("YYYY-MM-DD");

					const newRefMonthDate = moment(EVNInput.ref_monthData)
						.add(1, "month")
						.format("YYYY-MM-01");

					const nextMonthYYYYMM = moment(invoice.dateData)
						.add(1, "month")
						.format("YYYYMM");

					// Only insert the next-month EVN entry if it does not already exist
					GYAxios.get(`/site/${tableName}/evn_input/month/${nextMonthYYYYMM}`).then(
						(checkRes) => {
							const alreadyExists =
								!checkRes.data.error && checkRes.data.length > 0;
							if (!alreadyExists) {
								const userObj = {
									created_by_ID: user.sub,
									created_by_name: user.name,
									created_at: moment().format("YYYY-MM-DD HH:mm"),
									last_updated_ID: user.sub,
									last_updated_name: user.name,
									last_updated_at: moment().format("YYYY-MM-DD HH:mm"),
								};

								const param = {
									table_name: tableName,
									price_id: EVNInput.latest_price_id,
									start_date: newStartDate,
									ref_monthData: newRefMonthDate,
									...userObj,
								};

								GYAxios.post("/evn_input/new", param).then((res) => {
									console.log("Insert result :", res.data);
								});
							}
						},
					);
				}
			},
		);
	};

	/**
	 * Close preview of invoice
	 */
	const closePreview = () => {
		setPreviewInvoice(null);
		setSelectedSite(null);
		setShowGraph("fadeOut");
		// Revoke blob URL to free memory
		if (previewPdfUrl) {
			URL.revokeObjectURL(previewPdfUrl);
			setPreviewPdfUrl(null);
			setPreviewFileName("");
		}
		setPreviewLoading(false);
		setTimeout(() => {
			setPreviewIsHidden(true);
		}, 300);
	};

	/**
	 * Fetch a single invoice or technical report PDF from the server
	 * and display it in an iframe preview.
	 * @param {Object} site - the row site object from the invoice table
	 * @param {boolean} techReport - true for technical report, false for invoice
	 */
	const fetchPreviewPdf = async (site, techReport) => {
		// Open the preview overlay immediately with a loading state
		setPreviewInvoice(site);
		setIsTechReport(techReport);
		setIsCreditNote(false);
		setPreviewIsHidden(false);
		setPreviewLoading(true);
		setPreviewPdfUrl(null);
		setShowGraph("fadeIn");

		try {
			const dateData = moment(site.dateData).format("YYYY-MM-DD");
			const route = techReport
				? "/invoice/pdf/technical_report"
				: "/invoice/pdf/invoice";

			const response = await GYAxios.post(
				route,
				{ table_name: site.table_name, dateData },
				{ responseType: "blob", timeout: 60000 },
			);

			const blob = new Blob([response.data], { type: "application/pdf" });
			const blobUrl = URL.createObjectURL(blob);

			// Extract filename from Content-Disposition header or build a default
			let fileName = "";
			const disposition = response.headers["content-disposition"];
			if (disposition) {
				const match = disposition.match(/filename="?([^";\n]+)"?/);
				if (match) fileName = match[1];
			}
			if (!fileName) {
				fileName = techReport
					? `TechReport_${site.table_name}_${dateData}.pdf`
					: `Invoice_${site.table_name}_${dateData}.pdf`;
			}

			setPreviewPdfUrl(blobUrl);
			setPreviewFileName(fileName);
			setPreviewLoading(false);
		} catch (err) {
			console.error("Failed to fetch preview PDF:", err);
			setPreviewLoading(false);
			Noti.create({
				type: "e",
				msg:
					err.response?.data?.message ||
					"Failed to generate PDF preview. Please try again.",
			});
			closePreview();
		}
	};

	/**
	 * Download the currently previewed PDF
	 */
	const downloadPreviewPdf = () => {
		if (previewPdfUrl && previewFileName) {
			// Fetch the blob from the URL and save it
			fetch(previewPdfUrl)
				.then((res) => res.blob())
				.then((blob) => {
					FileSaver.saveAs(blob, previewFileName);
				});
		}
	};

	/**
	 * Close approval form component
	 */
	const closeForm = () => {
		setPreviewInvoice(null);
		setSelectedSite(null);
		setShowGraph("fadeOut");
		setTimeout(() => {
			setFormIsHidden(true);
		}, 300);
	};

	const closeConfirm = () => {
		setShowGraph("fadeOut");
		setEmailConfirmShown(false);
	};

	// Sets selected month to state
	// Based on selected month, check if the approval
	// is expired
	useEffect(() => {
		setSelectedMonth(props.selectedMonth);
		const selectedMonth = moment(props.selectedMonth).startOf("month");
		const currentMonth = moment().startOf("month");

		// Considered expired after 1 month from current month
		const isExpired = currentMonth.diff(selectedMonth, "month") > 1;

		if (isExpired) setApprovalIsExpired(true);
		else setApprovalIsExpired(false);

		return () => setApprovalIsExpired(false);
	}, [props.selectedMonth]);

	// Handle sites passed as invoice sites
	useEffect(() => {
		if (props.invoiceSites != null) {
			handleInvoiceSites(props.invoiceSites);
		}

		return () => setInvoiceSites([]);
	}, [props.invoiceSites, peakLabels]);

	// Sets invoices to preview
	const formatSelectedSite = (previewInvoice) => {
		if (previewInvoice != null) {
			const dateStr = selectedMonth.format("YYYY-MM-DD");
			const monthNumber = parseInt(moment(dateStr, "YYYY-MM-DD").format("M"));
			const monthStr = months[monthNumber - 1];

			let monthRange = {};

			switch (true) {
				// Jan - April
				case monthNumber >= 1 && monthNumber <= 4:
					monthRange = quartersMonth[0];
					break;
				// May - August
				case monthNumber >= 5 && monthNumber <= 8:
					monthRange = quartersMonth[1];
					break;
				// September - December
				default:
					monthRange = quartersMonth[2];
					break;
			}

			let docType = {};

			const yearMonth = moment(dateStr, "YYYY-MM-DD").format("YYMM");
			const monthYear = moment(dateStr, "YYYY-MM-DD")
				.endOf("month")
				.format("MMM-YYYY");
			const date = moment(dateStr, "YYYY-MM-DD")
				.endOf("month")
				.format("DD/MM/YYYY");
			const invIssuedDate = previewInvoice.inv_issued_date
				? moment(previewInvoice.inv_issued_date).format("DD/MM/YYYY")
				: date;
			const additional_info = previewInvoice.additional_info
				? JSON.parse(previewInvoice.additional_info)
				: null;
			const isMSA = previewInvoice.operation_status === 2;
			const dueDate = moment(invIssuedDate, "DD/MM/YYYY").add(
				previewInvoice.credit_term,
				"days",
			);

			if (previewInvoice.country === "TH") {
				// Thailand sites
				if (previewInvoice.company !== "Makro") {
					// CP ALL and others
					docType = {
						solarEntity: {
							name: previewInvoice.entity_name,
							taxID: previewInvoice.tax_id,
							entityID: previewInvoice.entity_id,
							address: previewInvoice.entity_address,
							accountantName: previewInvoice.contact_name,
							telephone: previewInvoice.telephone,
							email: previewInvoice.email,
						},
						bankAccount: {
							accName: previewInvoice.acc_name,
							accNumber: previewInvoice.acc_number,
							accNumRemark: previewInvoice.acc_num_remark,
							bankName: previewInvoice.bank_name,
							branch: previewInvoice.branch,
							swiftCode: previewInvoice.swift_code,
						},
						address: previewInvoice.address,
						company: previewInvoice.company,
						invNo: previewInvoice.invoice_no || buildInvoiceReceiptNumber({ // this is for later modification of invoice no. so invoice_no in database has higher priority
							type: "INV",
							solarEntityId: previewInvoice.entity_id,
							date: dateStr,
							siteCode: previewInvoice.site_code,
							isMSA,
						}),
						bdNo: "-",
						customerID: previewInvoice.customer_id,
						creditTerm: previewInvoice.credit_term,
						location: previewInvoice.location,
						taxID: previewInvoice.client_tax_id,
						BPAFee: previewInvoice.BPA_fee,
						monthRange,
						operation_status: previewInvoice.operation_status,
						approvalStatus: previewInvoice.approvalStatus,
						msa: previewInvoice.msa,
						msa_day: previewInvoice.msa_day,
						msa_description: previewInvoice.msa_description,
						msa_price: additional_info
							? additional_info.msa_price?.unit_price
							: 0,
						unit_price: previewInvoice.unit_price,
						existingEmailLog: additional_info?.existingEmailLog
							? additional_info.existingEmailLog
							: "",
					};
				} else {
					// Makro Billing Details
					let phase = "";
					const siteCode = previewInvoice.site_code;

					// Phase 2 and future phases of Makro
					if (siteCode.length > 5) phase = siteCode.substring(3, 4);

					docType = {
						company: previewInvoice.company,
						invNo: buildInvoiceReceiptNumber({
							type: "INV",
							solarEntityId: previewInvoice.entity_id,
							siteCode: `MKO${phase}`,
							date: dateStr,
							isMSA,
						}),
						bdNo: buildInvoiceReceiptNumber({
							type: "BD",
							solarEntityId: previewInvoice.entity_id,
							siteCode: `MKO${phase}`,
							date: dateStr,
							isMSA,
						}),
						siteName: previewInvoice.site_name,
						monthRange,
						operation_status: previewInvoice.operation_status,
						solarEntity: {
							name: previewInvoice.entity_name,
							taxID: previewInvoice.tax_id,
							entityID: previewInvoice.entity_id,
							address: previewInvoice.entity_address,
							accountantName: previewInvoice.contact_name,
							telephone: previewInvoice.telephone,
							email: previewInvoice.email,
						},
					};
				}
			} else {
				// Vietnam and Philippines
				docType = {
					solarEntity: {
						name: previewInvoice.entity_name,
						taxID: previewInvoice.tax_id,
						entityID: previewInvoice.entity_id,
						address: previewInvoice.entity_address,
						accountantName: previewInvoice.contact_name,
						telephone: previewInvoice.telephone,
						email: previewInvoice.email,
					},
					bankAccount: {
						accName: previewInvoice.acc_name,
						accNumber: previewInvoice.acc_number,
						accNumRemark: previewInvoice.acc_num_remark,
						bankName: previewInvoice.bank_name,
						branch: previewInvoice.branch,
						swiftCode: previewInvoice.swift_code,
					},
					company: previewInvoice.company,
					invNo: buildInvoiceReceiptNumber({
						type: "INV",
						solarEntityId: previewInvoice.entity_id,
						date: dateStr,
						siteCode: previewInvoice.site_code,
						isMSA,
					}),
					customerID: previewInvoice.customer_id,
					creditTerm: previewInvoice.credit_term,
					location: previewInvoice.location,
					taxID: previewInvoice.client_tax_id,
					contractID: previewInvoice.contract_id,
					projectID: previewInvoice.site_code,
					operation_status: previewInvoice.operation_status,
				};
			}

			const formattedData = {
				...docType,
				tariffData: {
					EAtariff: previewInvoice.EA_id,
					tariffID: previewInvoice.tariff_id,
					tariffDesc: previewInvoice.tariff_desc,
					EAEng: previewInvoice.name_eng,
					EAThai: previewInvoice.name_alt_lang,
				},
				clientID: previewInvoice.client_id,
				billingName: previewInvoice.billing_name,
				billingAddress: previewInvoice.billing_address,
				address: previewInvoice.address,
				priceON: previewInvoice.price_on,
				priceOFF: previewInvoice.price_off,
				priceSTD: previewInvoice.price_std,
				FTcost: previewInvoice.FT_cost,
				discountON: previewInvoice.ON_discount,
				discountOFF: previewInvoice.OFF_discount,
				discountSTD: previewInvoice.STD_discount,
				discountFT: previewInvoice.FT_discount,
				specialDiscount: previewInvoice.special_discount,
				specialComment: previewInvoice.special_discount_comment,
				tariffType: previewInvoice.tariff_type,
				EAtariff: previewInvoice.EA_tariff,
				solarON: previewInvoice.solar_ON,
				solarOFF: previewInvoice.solar_OFF,
				solarTotal: previewInvoice.solar_total,
				solarON_temp: previewInvoice.solar_ON_temp ?? null,
				solarOFF_temp: previewInvoice.solar_OFF_temp ?? null,
				solarSTD_temp: previewInvoice.solar_STD_temp ?? null,
				deliveredTotal: previewInvoice.delivered_total,
				deliveredOn: previewInvoice.delivered_ON,
				deliveredOff: previewInvoice.delivered_OFF,
				deliveredTotal_temp: previewInvoice.E_delivered_total_temp ?? null,
				deliveredOn_temp: previewInvoice.E_delivered_ON_temp ?? null,
				deliveredOff_temp: previewInvoice.E_delivered_OFF_temp ?? null,
				deliveredSTD_temp: previewInvoice.E_delivered_STD_temp ?? null,
				isGridReinjectApplied:
					previewInvoice.is_grid_reinject_applied === 1 ? true : false,
				invTopRightNote: previewInvoice.inv_tr_key_val_note
					? JSON.parse(previewInvoice.inv_tr_key_val_note)
					: null,
				dateStr: monthYear,
				date,
				dueDate: dueDate.format("DD/MM/YYYY"),
				selectedMonth,
				monthStr,
				invIssuedDate,
				siteName: previewInvoice.site_name,
				siteCode: previewInvoice.site_code,
				solo: previewInvoice.solo === 1 ? true : false,
				country: previewInvoice.country,
				cod: previewInvoice.cod,
				power: previewInvoice.power,
				city: previewInvoice.city,
				solarModuleModel: previewInvoice.solar_modules_model,
				noSolarModules: previewInvoice.nb_solar_modules,
				siteID: previewInvoice.site_id,
				currency: previewInvoice.currency,
				vat: previewInvoice.vat,
				isEDMI: previewInvoice.EDMI_meter === 1 ? true : false,
				showUnused: previewInvoice.show_unused === 1 ? true : false,
				additional_info,
				old_price:
					previewInvoice.price_start_date != null
						? moment(previewInvoice.price_start_date).format("DD") != "01" &&
							moment(previewInvoice.price_start_date).format("MMYYYY") ==
								moment(selectedMonth).format("MMYYYY")
						: false,
				unit_price: previewInvoice.unit_price || null,
				customInvTemplate: previewInvoice.custom_inv_template || null,
				items: previewInvoice.items || [],
			};

			const selectedSite = {
				label: previewInvoice.site_name,
				table_name: previewInvoice.table_name,
				value: formattedData,
			};

			return selectedSite;
		}
		return null;
	};

	// ============================================================================
	// Server-side email sending function (replaces old client-side pipeline)
	// ============================================================================
	const sendEmailServerSide = async (site) => {
		setBulkDownloadProgress({
			text: "Generating documents and sending email...",
			allCount: 1,
			loadedCount: 0,
			startTime: new Date().getTime(),
		});

		try {
			const payload = {
				table_name: site.table_name,
				dateData: moment(site.dateData).format('YYYY-MM-DD'),
				site_id: site.site_id,
				monthly_id: site.id,
				recipients: emailsRecipients,
				saved: reSaveRecipients,
				email_sender: user.email,
				approval_status: site.approvalStatus,
			};

			const res = await GYAxios.post(
				"/invoice/send_email_server_side",
				payload,
				{ timeout: 120000 },
			);

			setReSaveRecipients(false);
			if (res.status === 200) {
				Noti.create({ type: "s", msg: res.data.message });
			} else {
				Noti.create({ type: "e", msg: res.data.message });
			}

			setTimeout(async () => {
				resetBulkDownloadStates();
				try {
					const logRes = await GYAxios.get(
						"/invoice/email_logs?month=" + selectedMonth.format("YYYY-MM"),
					);
					if (logRes.status === 200) {
						setExistingEmailLog(logRes.data);
					} else {
						setExistingEmailLog(null);
						Noti.create({
							type: "e",
							msg: "Failed to get Email log - Button for emailing cannot be used!",
						});
					}
				} catch (logErr) {
					setExistingEmailLog(null);
				}
				props.setReady(false);
			}, 1000);
		} catch (err) {
			setReSaveRecipients(false);
			const msg =
				err.response?.data?.message ||
				err.response?.statusText ||
				"Please contact IT for this error.";
			Noti.create({ type: "e", msg });
			resetBulkDownloadStates();
		}
	};

	const findEmailToSend = async (site) => {
		setEmailsRecipients([]);
		try {
			await GYAxios.get(
				`/invoice/get_site_email_add/site_id/${site.site_id}`,
			).then((res) => {
				if (res.data?.error != null) {
					Noti.create({ type: "e", msg: "Error while getting site contacts!" });
					return;
				} else {
					const foundEmailAdds = [];
					if (res.data.length === 0) {
						Noti.create({ type: "w", msg: "No email found!" });
					} else {
						// example of res.data: ["_acctgroup@alfredo.co.th_,_sermkiat@alfredo.co.th_,_kriengsak@alfredo.co.th_,_thanakorn@alfredo.co.th_"]
						const refinedEmails = res.data.map((emails) => {
							// Split emails by comma
							const emailArr = emails.split(",");
							// Remove the first and last underscore from each email
							const refinedEmailArr = emailArr.map((email) => {
								// Use regex to replace only the first and last underscore
								return email.replace(/^_/, "").replace(/_$/, "");
							});
							return refinedEmailArr;
						});
						foundEmailAdds.push(...refinedEmails);
					}

					setShowGraph("fadeIn");
					setEmailsRecipients(foundEmailAdds);
					setEmailConfirmShown(true);
					emailsSite.current = site;
				}
			});
		} catch (error) {
			console.log("error :", error);
			Noti.create({ type: "e", msg: "Error while getting site contacts!" });
			return;
		}
	};

	useEffect(() => {
		if (typeof emailsRecipients === "string" && emailsSite.current !== null) {
			sendEmailServerSide(emailsSite.current);
		}
	}, [emailsRecipients]);

	return (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row>
						<Col md="6">
							<h2 className="panel-title" id="date">
								Invoice Overview - {selectedMonth.format("MMMM YYYY")}
							</h2>
							<br />
							<div className="month-selector">
								Choose another Month: &nbsp;&nbsp;
								<DatePicker
									minDate={localTimeToPickerTime(minDate)}
									maxDate={localTimeToPickerTime(maxDate)}
									selected={localTimeToPickerTime(selectedMonth.toDate())}
									onChange={(date) => {
										setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setSelectedMonth(moment(pickerTimeTolocalTime(date)));
										props.setReady(false);
									}}
									dateFormat="MMM yyyy"
									showMonthYearPicker
								/>
							</div>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							<img
								src={`${directory}/images/reload.png`}
								onClick={() => reload()}
								style={{
									height: "35px",
									cursor: "pointer",
								}}
								alt="logo"
							/>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							{isGYUser(roles) && (
								<Select
									value={countries.filter((option) => option.value === country)}
									options={countries}
									onChange={(object) => {
										props.setCountry(object.value);
										setCountry(object.value);
										props.setReady(false);
									}}
									defaultValue={countries.filter(
										(option) => option.value === loggedCountry,
									)}
									styles={selectStyle}
								/>
							)}
							{isGYUser(roles) && country === "VN" && (
								<Select
									value={selectedEntity}
									options={entityOpts}
									onChange={(object) => {
										setSelectedEntity(object);
									}}
									styles={selectStyle}
								/>
							)}
						</Col>
					</Row>
					{isShownBulkActions && !!selectedRows.length && (
						<div style={{ marginTop: "1em" }}>
							<div className="invoice-bulk-actions-acct-th">
								{isShownCombinedDownload && (
									<button
										className="btn btn-primary"
										onClick={() => onBulkDownloadServerSide(BULK_ACTIONS.COMBINED)}
										disabled={isBulkDownloading}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.COMBINED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction === BULK_ACTIONS.COMBINED &&
											bulkDownloadProgress?.text) ||
											"Combined Download"}
										<Popover
											content={
												<div>
													This will combine:
													<ul>
														<li>Original Invoice / Billing Detail</li>
														<li>Copy Invoice / Billing Detail</li>
														<li>O&M Report</li>
														{(isGYUser(roles) ||
															isMakroClientUser(userInfo)) && (
															<>
																<li>
																	Original Makro Invoice (if all Makro sites
																	selected)
																</li>
																<li>
																	Copy Makro Invoice (if all makro sites
																	selected)
																</li>
															</>
														)}
													</ul>
													of selected sites in 1 PDF
													<br />
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
								{isShownSeparateDownload && (
									<button
										className="btn btn-primary"
										onClick={() => onBulkDownloadServerSide(BULK_ACTIONS.SEPARATED)}
										disabled={isBulkDownloading}
										style={{ marginLeft: "1em" }}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.SEPARATED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.SEPARATED &&
											bulkDownloadProgress?.text) ||
											"Separated Download"}
										<Popover
											content={
												<div>
													This will download a ZIP file containing:
													<ul>
														<li>
															PDFs of Original Invoice / Billing Detail for each
															site selected (1 PDF / 1 site)
														</li>
														<li>
															PDFs of Copy Invoice / Billing Detail for each
															site selected (1 PDF / 1 site)
														</li>
														<li>
															PDFs of O&M Report for each site selected (1 PDF /
															1 site)
														</li>
														{(isGYUser(roles) ||
															isMakroClientUser(userInfo)) && (
															<>
																<li>
																	PDFs of Original Makro Invoices if all Makro
																	sites selected (1 PDF / Makro phase)
																</li>
																<li>
																	PDFs of Copy Makro Invoices if all Makro sites
																	selected (1 PDF / Makro phase)
																</li>
															</>
														)}
														<li>
															1 PDF of Copy Invoices / Billing Details{" "}
															{(isGYUser(roles) ||
																isMakroClientUser(userInfo)) &&
																"and Makro Invoices"}
														</li>
													</ul>
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
								{isShownClientGroupedDownload && (
									<button
										className="btn btn-primary"
										onClick={() =>
											onBulkDownloadServerSide(BULK_ACTIONS.CLIENT_GROUPED)
										}
										disabled={isBulkDownloading}
										style={{ marginLeft: "1em" }}
									>
										{isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.CLIENT_GROUPED && <Spin></Spin>}
										{(isBulkDownloading?.bulkAction ===
											BULK_ACTIONS.CLIENT_GROUPED &&
											bulkDownloadProgress?.text) ||
											"Client Grouped Download"}
										<Popover
											content={
												<div>
													This will download a ZIP file containing:
													<ul>
														<li>
															PDFs of Proforma Invoice for each site selected (1
															PDF / 1 site)
														</li>
														<li>
															PDFs of O&M Report for each site selected (1 PDF /
															1 site)
														</li>
													</ul>
													{isGYUser(roles) && (
														<div>
															All PDFs are grouped by client in a folder for
															that client
														</div>
													)}
													{isGYUser(roles) && (
														<b>
															NOTE: Invoices that have not yet been approved by
															ACCT team <br /> will not be included in the
															download.
														</b>
													)}
												</div>
											}
										>
											<Help className="help-popover-icon" />
										</Popover>
									</button>
								)}
							</div>
						</div>
					)}
				</div>
				<div className="panel-body">
					<MaterialTable
						tableRef={matTbRef}
						columns={columns}
						data={invoicesFilter(invoiceSites)}
						isLoading={!props.ready}
						onSelectionChange={(rows) => setSelectedRows(rows)}
						localization={{
							body: {
								emptyDataSourceMessage: (
									<p style={{ fontSize: "1.5rem" }}>
										Invoices are still under review
									</p>
								),
							},
							grouping: {
								placeholder: "Drag 'Client' header to group by clients",
							},
						}}
						options={{
							showTitle: false,
							selection: isShownBulkActions,
							grouping: true,
							pageSize: 10,
							pageSizeOptions: [10, 20, 40, 60],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
						}}
					/>
				</div>
			</div>
			{position != null && showRemark != null && (
				<div
					className="reject-remark"
					style={{ top: position.y + 20, left: 0 }}
				>
					<p>
						<b>
							Rejected{" "}
							{approvalPermissions.level === "IT" && showRemark.rejectedByMsg} :
						</b>{" "}
						<i>
							{showRemark.rejectedRemark != null
								? showRemark.rejectedRemark
								: "-"}
						</i>
					</p>
				</div>
			)}
			{/* Invoice preview */}
			{!previewIsHidden && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{ textAlign: "center", marginTop: "25px" }}
						className={showGraph}
					>
						{isCreditNote ? (
							/* Credit notes still use client-side rendering (legacy, only 2023-09) */
							selectedSite != null ? (
								<InvoicePreview
									isTechReport={false}
									selectedSite={selectedSite}
									isCreditNote={true}
									getSiteData={getSiteData}
									calculateForChangingTariff={calculateForChangingTariff}
								/>
							) : (
								<LoaderGraph style={{ height: "72vh" }} />
							)
						) : previewLoading ? (
							<LoaderGraph style={{ height: "72vh" }} />
						) : previewPdfUrl ? (
							<div>
								<iframe
									title="PDF Preview"
									src={previewPdfUrl}
									style={{
										width: "100%",
										height: "72vh",
										border: "none",
										backgroundColor: "#fff",
									}}
								/>
								<div style={{ marginTop: "10px" }}>
									<Button
										variant="primary"
										onClick={downloadPreviewPdf}
										style={{ marginRight: "10px" }}
									>
										Download
									</Button>
								</div>
							</div>
						) : (
							<LoaderGraph style={{ height: "72vh" }} />
						)}
						<Button variant="light" onClick={closePreview} className="closeBtn">
							Close
						</Button>
					</div>
				</div>
			)}
			{/* Extra line insert pop up */}
			{showInputForm ? (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						style={{
							marginTop: "25px",
							maxHeight: "85vh",
							overflowY: "auto",
						}}
						className="fadeIn"
					>
						<ExtraLineForm
							ready={props.setReady}
							closeForm={() => setShowInputForm(false)}
							site={selectedSite}
							month={selectedMonth}
						/>
					</div>
				</div>
			) : null}
			{/* Approval form preview */}
			{!formIsHidden && (
				<div className="overlay translucent">
					<div id="form-container" className={showGraph}>
						{selectedSite != null ? (
							<ApprovalContent
								closeForm={closeForm}
								updateInvoiceStatus={updateInvoiceStatus}
								selectedSite={selectedSite}
								showVatInvoiceForm={showVatInvoiceForm}
								process={process}
							/>
						) : (
							<LoaderGraph style={{ height: "72vh" }} />
						)}
					</div>
				</div>
			)}
			{emailConfirmShown && (
				<div className="overlay translucent">
					<div id="form-container" className={showGraph}>
						{emailsRecipients.length >= 0 && (
							<EmailConfirm
								closeForm={closeConfirm}
								recipients={emailsRecipients}
								setNewRecipients={setEmailsRecipients}
								reFetchTable={props.setReady}
								saveNewRecipients={setReSaveRecipients}
							/>
						)}
					</div>
				</div>
			)}

			{/* For bulk download loading overlay */}
			{bulkDownloadProgress && (
				<div className="loading-overlay">
					<div className="spinner"></div>
					<div className="text">{bulkDownloadProgress.text}</div>
					{bulkDownloadProgressWarning && (
						<div
							className="text"
							style={{ color: "#d7ff46", fontWeight: "bold" }}
						>
							{bulkDownloadProgressWarning}
						</div>
					)}
				</div>
			)}

			{versionHistoryInvoice && (
				<div className="overlay translucent">
					<div
						id="graphContainer"
						className="fadeIn"
						style={{ marginTop: "15px", maxHeight: "85vh", overflowY: "auto", width: "60%" }}
					>
						<VersionHistoryModal
							monthlyCalcId={versionHistoryInvoice.id}
							siteName={versionHistoryInvoice.site_name}
							onClose={() => setVersionHistoryInvoice(null)}
						/>
					</div>
				</div>
			)}
		</>
	);
}

export default InvoiceTable;
